import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Eye, EyeOff, Lock, Minus, Plus, Printer, RotateCcw, Trash2, WifiOff, X } from "lucide-react";
import { toast } from "sonner";
import { useProducts } from "@/context/ProductsContext";
import { LEMONADE_ADDONS } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gb-admin-token";
const QUEUE_KEY = "gb-pos-queue";
const SQ_PENDING_KEY = "gb-pos-square-pending";
const TAX_RATE = 0.0725;
const ICE_LEVELS = ["No Ice", "Light", "Regular"];
const TABS = [["lemonade", "🍋 Lemonade"], ["glow", "✨ Glow"], ["bling", "💎 Bling"]];
const TIP_OPTIONS = [[0, "No tip"], [10, "10%"], [15, "15%"], [20, "20%"]];

const keyOf = (id, variant) => `${id}|${variant || ""}`;
const readQueue = () => {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY)) || []; } catch { return []; }
};

const PosLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.email);
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111] p-8 space-y-5" data-testid="pos-login-form">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-pink-300" />
          </span>
          <div>
            <h1 className="font-extrabold text-xl text-white">POS Sign In</h1>
            <p className="text-sm text-zinc-500">In-person sales register</p>
          </div>
        </div>
        <input data-testid="pos-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
          className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none" />
        <div className="relative">
          <input data-testid="pos-login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-3 !pr-11 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none" />
          <button type="button" data-testid="pos-login-toggle-password" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button data-testid="pos-login-submit" type="submit" disabled={loading} className="w-full rounded-lg bg-white text-black font-bold text-sm py-3 uppercase tracking-widest hover:bg-zinc-200 disabled:opacity-50">
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const ConfigModal = ({ product, flavorNames, onClose, onAdd }) => {
  const [ice, setIce] = useState("Regular");
  const [sweetener, setSweetener] = useState("regular");
  const [addons, setAddons] = useState([]);
  const [picks, setPicks] = useState([]);
  const [qty, setQty] = useState(1);
  const needsPicks = product.pick > 0;
  const picksReady = !needsPicks || (picks.length === product.pick && picks.every(Boolean));

  const chosen = LEMONADE_ADDONS.filter((a) => addons.includes(a.id));
  const unit = product.price + chosen.reduce((s, a) => s + a.price, 0);

  const toggleAddon = (id) => setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  const setPick = (i, val) => setPicks((prev) => { const n = [...prev]; n[i] = val; return n; });

  const confirm = () => {
    const parts = needsPicks ? picks.map((p, i) => `#${i + 1} ${p}`) : [`Ice: ${ice}`];
    parts.push(sweetener === "keto" ? "Keto / Diabetic sweetener" : "Regular sugar");
    chosen.forEach((a) => parts.push(`+${a.name}`));
    onAdd({ ...product, price: unit }, parts.join(" · "), qty, addons);
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        data-testid="pos-config-modal"
        className="w-full max-w-md rounded-2xl bg-[#161616] border border-white/15 p-6 space-y-5 max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-extrabold text-lg text-white leading-tight">{product.name}</h3>
            <p className="text-amber-300 font-bold mt-1">${unit.toFixed(2)} each</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {needsPicks ? (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Pick {product.pick} flavors</p>
            {Array.from({ length: product.pick }).map((_, i) => (
              <select key={i} data-testid={`pos-pick-${i}`} value={picks[i] || ""} onChange={(e) => setPick(i, e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-black/40 px-3 py-3 text-white focus:border-pink-400 focus:outline-none">
                <option value="">#{i + 1} — Select flavor —</option>
                {flavorNames.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            ))}
          </div>
        ) : (
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">🧊 Ice level</p>
            <div className="grid grid-cols-3 gap-2">
              {ICE_LEVELS.map((l) => (
                <button key={l} data-testid={`pos-ice-${l.toLowerCase().replace(" ", "-")}`} onClick={() => setIce(l)}
                  className={`rounded-xl border py-3 text-sm font-bold transition-colors ${ice === l ? "bg-pink-500 border-pink-500 text-white" : "border-white/15 text-zinc-400"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}

        <div data-testid="pos-sweetener-section">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">🍯 Sweetener</p>
          <div className="grid grid-cols-2 gap-2">
            <label data-testid="pos-sweetener-regular" className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-sm font-bold cursor-pointer transition-colors ${sweetener === "regular" ? "border-pink-500 bg-pink-500/15 text-white" : "border-white/15 text-zinc-400"}`}>
              <input type="checkbox" checked={sweetener === "regular"} onChange={() => setSweetener("regular")} className="w-4 h-4 accent-pink-500" />
              Regular sugar
            </label>
            <label data-testid="pos-sweetener-keto" className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-sm font-bold cursor-pointer transition-colors ${sweetener === "keto" ? "border-pink-500 bg-pink-500/15 text-white" : "border-white/15 text-zinc-400"}`}>
              <input type="checkbox" checked={sweetener === "keto"} onChange={() => setSweetener("keto")} className="w-4 h-4 accent-pink-500" />
              Diabetic / Keto
            </label>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Add-ons</p>
          <div className="grid grid-cols-2 gap-2">
            {LEMONADE_ADDONS.map((a) => (
              <button key={a.id} data-testid={`pos-addon-${a.id}`} onClick={() => toggleAddon(a.id)}
                className={`rounded-xl border px-3 py-3 text-sm text-left transition-colors ${addons.includes(a.id) ? "border-pink-500 bg-pink-500/15 text-white" : "border-white/15 text-zinc-400"}`}>
                {a.emoji} {a.name}
                <span className="block text-xs text-zinc-500">+${a.price.toFixed(2)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="pos-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 rounded-xl border border-white/15 text-white flex items-center justify-center" aria-label="Less"><Minus className="w-4 h-4" /></button>
            <span className="text-xl font-extrabold text-white w-6 text-center" data-testid="pos-qty">{qty}</span>
            <button data-testid="pos-qty-plus" onClick={() => setQty(qty + 1)} className="w-11 h-11 rounded-xl border border-white/15 text-white flex items-center justify-center" aria-label="More"><Plus className="w-4 h-4" /></button>
          </div>
          <button data-testid="pos-add-to-ticket" onClick={confirm} disabled={!picksReady}
            className="rounded-xl bg-pink-500 text-white font-bold px-6 py-3.5 disabled:opacity-40 active:scale-95 transition-transform">
            Add · ${(unit * qty).toFixed(2)}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ReportModal = ({ report, onClose }) => (
  <motion.div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
    <motion.div data-testid="z-report-modal" className="w-full max-w-md rounded-2xl bg-[#161616] border border-white/15 p-6 space-y-5"
      initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-extrabold text-xl text-white">🧾 End of Day</h3>
          <p className="text-sm text-zinc-500">{report.date}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.print()} data-testid="z-report-print-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-zinc-300 hover:border-white/50" aria-label="Print report">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/10 bg-black/30 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sales</p>
          <p className="text-2xl font-extrabold text-white" data-testid="z-orders">{report.orders}</p>
        </div>
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">Gross</p>
          <p className="text-2xl font-extrabold text-white" data-testid="z-gross">${report.gross.toFixed(2)}</p>
        </div>
      </div>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between text-zinc-400"><dt>💵 Cash ({report.cash.count})</dt><dd className="text-white font-bold" data-testid="z-cash">${report.cash.total.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-400"><dt>💳 Card ({report.card.count})</dt><dd className="text-white font-bold" data-testid="z-card">${report.card.total.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-500 pl-5"><dt>↳ Tap / Reader ({report.card_reader.count})</dt><dd data-testid="z-reader">${report.card_reader.total.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-500 pl-5"><dt>↳ Keyed in ({report.card_keyed.count})</dt><dd data-testid="z-keyed">${report.card_keyed.total.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-500 pl-5"><dt>↳ Square Tap ({report.card_square_tap.count})</dt><dd data-testid="z-square-tap">${report.card_square_tap.total.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-400 pt-2 border-t border-white/10"><dt>💛 Tips</dt><dd className="text-white" data-testid="z-tips">${report.tips.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-400"><dt>Tax collected</dt><dd className="text-white">${report.tax.toFixed(2)}</dd></div>
        <div className="flex justify-between text-zinc-400"><dt>Items sold</dt><dd className="text-white">{report.units}</dd></div>
      </dl>
      <p className="text-xs text-zinc-500">Drawer count: expect <span className="text-amber-300 font-bold">${report.cash.total.toFixed(2)}</span> in cash from today's register sales.</p>
    </motion.div>
    {createPortal(
      <div id="z-report-print-portal">
        <div className="print-form-body">
          <h2>END OF DAY — {report.date}</h2>
          <p>Lani Lemonade / Glow &amp; Bling · POS Register</p>
          <table cellPadding="4" style={{ width: "100%", marginTop: "10pt", borderCollapse: "collapse" }}>
            <tbody>
              <tr><td>Sales</td><td style={{ textAlign: "right" }}>{report.orders}</td></tr>
              <tr><td><strong>Gross</strong></td><td style={{ textAlign: "right" }}><strong>${report.gross.toFixed(2)}</strong></td></tr>
              <tr><td>Cash ({report.cash.count})</td><td style={{ textAlign: "right" }}>${report.cash.total.toFixed(2)}</td></tr>
              <tr><td>Card ({report.card.count})</td><td style={{ textAlign: "right" }}>${report.card.total.toFixed(2)}</td></tr>
              <tr><td style={{ paddingLeft: "12pt" }}>↳ Tap / Reader ({report.card_reader.count})</td><td style={{ textAlign: "right" }}>${report.card_reader.total.toFixed(2)}</td></tr>
              <tr><td style={{ paddingLeft: "12pt" }}>↳ Keyed in ({report.card_keyed.count})</td><td style={{ textAlign: "right" }}>${report.card_keyed.total.toFixed(2)}</td></tr>
              <tr><td style={{ paddingLeft: "12pt" }}>↳ Square Tap ({report.card_square_tap.count})</td><td style={{ textAlign: "right" }}>${report.card_square_tap.total.toFixed(2)}</td></tr>
              <tr><td>Tips</td><td style={{ textAlign: "right" }}>${report.tips.toFixed(2)}</td></tr>
              <tr><td>Tax collected</td><td style={{ textAlign: "right" }}>${report.tax.toFixed(2)}</td></tr>
              <tr><td>Items sold</td><td style={{ textAlign: "right" }}>{report.units}</td></tr>
            </tbody>
          </table>
          <p style={{ marginTop: "12pt", fontWeight: 800 }}>Cash drawer: expect ${report.cash.total.toFixed(2)}</p>
          <p style={{ marginTop: "14pt" }}>Counted: $ <span className="pf-line"></span></p>
          <p style={{ marginTop: "8pt" }}>Counted by: <span className="pf-line"></span></p>
        </div>
      </div>,
      document.body
    )}
  </motion.div>
);

export default function PosPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));
  const { products } = useProducts();
  const [tab, setTab] = useState("lemonade");
  const [ticket, setTicket] = useState([]);
  const [modal, setModal] = useState(null);
  const [customer, setCustomer] = useState("");
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(null);
  const [cardChooser, setCardChooser] = useState(false);
  const [tipPct, setTipPct] = useState(0);
  const [tipCustom, setTipCustom] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(readQueue);
  const [report, setReport] = useState(null);
  const syncing = useRef(false);

  const authH = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, authH())
      .then((r) => setEmail(r.data.email))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); })
      .finally(() => setChecking(false));
  }, [token]);

  // Returning from the Square app after a Tap to Pay charge
  useEffect(() => {
    if (!email) return;
    const raw = new URLSearchParams(window.location.search).get("data");
    if (!raw) return;
    navigate("/pos", { replace: true });
    let result = null;
    try { result = JSON.parse(raw); } catch {
      toast.error("Unreadable response from Square");
      return;
    }
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem(SQ_PENDING_KEY)); } catch {}
    localStorage.removeItem(SQ_PENDING_KEY);
    if (!pending || result.state !== pending.order_id) {
      toast.error("Square returned, but no matching sale was waiting on this iPad");
      return;
    }
    if (result.status === "ok") {
      completeSquareSale(pending, result.transaction_id || result.client_transaction_id || "");
    } else {
      setTicket(pending.items);
      setCustomer(pending.customer_name === "Walk-in" ? "" : pending.customer_name);
      setCardChooser(true);
      toast.error(result.error_code === "payment_canceled"
        ? "Payment canceled in Square — ticket restored"
        : `Square error: ${result.error_code || "unknown"} — ticket restored`);
    }
  }, [email]);

  useEffect(() => {
    const sync = async () => {
      if (syncing.current || !navigator.onLine) return;
      const q = readQueue();
      if (!q.length) return;
      syncing.current = true;
      const remaining = [];
      for (const item of q) {
        try {
          await axios.post(`${API}/pos/orders`, {
            customer_name: item.customer_name, payment_method: item.payment_method, card_entry: item.card_entry,
            tip_pct: item.tip_pct || 0, tip_amount: item.tip_amount || 0,
            order_id: item.order_id, created_offline_at: item.created_offline_at,
            items: item.items.map((t) => ({ id: t.id, qty: t.qty, variant: t.variant || "", addons: t.addons || [] })),
          }, authH());
        } catch (e) {
          if (!e.response || e.response.status >= 500) remaining.push(item);
        }
      }
      localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
      setQueue(remaining);
      syncing.current = false;
      if (remaining.length < q.length) toast.success(`${q.length - remaining.length} offline sale${q.length - remaining.length === 1 ? "" : "s"} synced`);
    };
    sync();
    const onOnline = () => { setOnline(true); sync(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, [queue.length]);

  const sellable = useMemo(
    () => products.filter((p) => p.brand === tab && p.price != null && p.in_stock !== false),
    [products, tab]
  );

  const flavorNames = useMemo(
    () => products.filter((p) => p.brand === "lemonade" && ["Classic", "Flavored"].includes(p.cat) && p.in_stock !== false).map((p) => p.name.replace(" — 34oz", "")),
    [products]
  );

  const addToTicket = (product, variant, qty, addons) => {
    const key = keyOf(product.id, variant);
    setTicket((prev) => {
      const found = prev.find((t) => t.key === key);
      if (found) return prev.map((t) => (t.key === key ? { ...t, qty: t.qty + qty } : t));
      return [...prev, { ...product, key, variant, addons, qty }];
    });
  };

  const tapProduct = (p) => {
    if (p.brand === "lemonade") setModal(p);
    else addToTicket(p, p.size ? `Size: ${p.size}` : "", 1, []);
  };

  const setQty = (key, qty) => {
    if (qty <= 0) setTicket((prev) => prev.filter((t) => t.key !== key));
    else setTicket((prev) => prev.map((t) => (t.key === key ? { ...t, qty } : t)));
  };

  const subtotal = ticket.reduce((s, t) => s + t.price * t.qty, 0);
  const tax = subtotal * TAX_RATE;
  const customActive = tipCustom !== "";
  const customTipAmt = customActive ? Math.min(parseFloat(tipCustom) || 0, 500) : 0;
  const tip = customActive ? customTipAmt : (subtotal * tipPct) / 100;
  const total = subtotal + tax + tip;

  const navigate = useNavigate();

  const finishSale = (data, method, offline, items = ticket) => {
    const hasLemonade = items.some((t) => t.brand === "lemonade");
    setPaid({ ...data, method, offline, hasLemonade });
    setTicket([]);
    setCustomer("");
    setCardChooser(false);
    setTipPct(0);
    setTipCustom("");
    if (hasLemonade) {
      toast.success("🖨️ Printing cup stickers…");
      setTimeout(() => navigate(`/print/${data.order_id}`), 900);
    }
  };

  const queueSale = (snapshot) => {
    const q = [...readQueue(), snapshot];
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    setQueue(q);
    finishSale({ order_id: snapshot.order_id, total: snapshot.total }, snapshot.payment_method, true);
    toast.success("Saved on this iPad — syncs when back online");
  };

  const charge = async (method, cardEntry = null) => {
    if (!ticket.length) return;
    const effectiveTipPct = method === "card" && !customActive ? tipPct : 0;
    const effectiveTipAmt = method === "card" && customActive ? customTipAmt : 0;
    const snapshot = {
      order_id: crypto.randomUUID(),
      customer_name: customer.trim() || "Walk-in",
      payment_method: method,
      card_entry: cardEntry,
      tip_pct: effectiveTipPct,
      tip_amount: effectiveTipAmt,
      created_offline_at: new Date().toISOString(),
      items: ticket,
      total: +(subtotal + tax + (method === "card" ? tip : 0)).toFixed(2),
    };
    if (!navigator.onLine) {
      queueSale(snapshot);
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/pos/orders`, {
        customer_name: snapshot.customer_name, payment_method: method, card_entry: cardEntry,
        tip_pct: effectiveTipPct, tip_amount: effectiveTipAmt,
        order_id: snapshot.order_id, created_offline_at: snapshot.created_offline_at,
        items: ticket.map((t) => ({ id: t.id, qty: t.qty, variant: t.variant || "", addons: t.addons || [] })),
      }, authH());
      finishSale(data, method, false);
      toast.success(`${method === "cash" ? "Cash" : "Card"} sale recorded — $${data.total.toFixed(2)}`);
    } catch (e) {
      if (e.response) {
        toast.error(e.response.data?.detail || "Could not record sale");
      } else {
        queueSale(snapshot);
      }
    } finally {
      setPaying(false);
      setCardChooser(false);
    }
  };

  const openReport = async () => {
    try {
      const { data } = await axios.get(`${API}/pos/z-report`, authH());
      setReport(data);
    } catch {
      toast.error("Could not load today's report — are you online?");
    }
  };

  const startSquareTap = () => {
    if (!ticket.length) return;
    const pending = {
      order_id: crypto.randomUUID(),
      customer_name: customer.trim() || "Walk-in",
      tip_pct: !customActive ? tipPct : 0,
      tip_amount: customActive ? customTipAmt : 0,
      items: ticket,
      total: +total.toFixed(2),
    };
    localStorage.setItem(SQ_PENDING_KEY, JSON.stringify(pending));
    const payload = {
      amount_money: { amount: String(Math.round(total * 100)), currency_code: "USD" },
      callback_url: `${window.location.origin}/pos`,
      client_id: process.env.REACT_APP_SQUARE_APPLICATION_ID,
      version: "1.3",
      options: { supported_tender_types: ["CREDIT_CARD"] },
      notes: `Lani POS — ${pending.customer_name}`,
      location_id: process.env.REACT_APP_SQUARE_LOCATION_ID,
      state: pending.order_id,
    };
    window.location.assign(`square-commerce-v1://payment/create?data=${encodeURIComponent(JSON.stringify(payload))}`);
  };

  const completeSquareSale = async (pending, txn) => {
    try {
      const { data } = await axios.post(`${API}/pos/orders`, {
        customer_name: pending.customer_name, payment_method: "card", card_entry: "square_tap",
        tip_pct: pending.tip_pct || 0, tip_amount: pending.tip_amount || 0,
        order_id: pending.order_id, square_txn: txn || undefined,
        items: pending.items.map((t) => ({ id: t.id, qty: t.qty, variant: t.variant || "", addons: t.addons || [] })),
      }, authH());
      finishSale(data, "card", false, pending.items);
      toast.success(`Square payment approved — $${data.total.toFixed(2)}`);
    } catch {
      setTicket(pending.items);
      toast.error("Card was charged in Square but the order didn't save — ticket restored. Log it with the Tap / Reader button so the customer isn't charged twice.");
    }
  };

  if (checking) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!token || !email) return <PosLogin onLogin={(t, e) => { setToken(t); setEmail(e); }} />;

  return (
    <main className="flex flex-col lg:flex-row min-h-[calc(100vh-4rem)]" data-testid="pos-register">
      <div className="flex-1 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-white">POS Register</h1>
            <p className="text-xs text-zinc-500">Signed in as {email} · <a href="/admin" className="underline underline-offset-2 hover:text-white" data-testid="pos-admin-link">Admin</a></p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {TABS.map(([k, l]) => (
              <button key={k} data-testid={`pos-tab-${k}`} onClick={() => setTab(k)}
                className={`rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-pink-500 text-white" : "border border-white/15 text-zinc-400"}`}>
                {l}
              </button>
            ))}
            <button data-testid="pos-test-label" onClick={() => navigate("/print/test")}
              className="rounded-full px-4 py-2.5 text-sm font-bold border border-white/15 text-zinc-300 hover:border-white/40 transition-colors inline-flex items-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Test Label
            </button>
            <button data-testid="pos-eod-button" onClick={openReport}
              className="rounded-full px-4 py-2.5 text-sm font-bold border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors">
              🧾 End of Day
            </button>
          </div>
        </div>

        {!online && (
          <div className="mb-4 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 flex items-center gap-3" data-testid="pos-offline-banner">
            <WifiOff className="w-4 h-4 text-amber-300 shrink-0" />
            <p className="text-sm text-amber-200">You're offline — sales save on this iPad and sync when you're back online. Stickers still print.</p>
          </div>
        )}
        {queue.length > 0 && (
          <div className="mb-4 rounded-xl border border-white/15 bg-white/5 px-4 py-3" data-testid="pos-pending-sync">
            <p className="text-sm text-zinc-300">⏳ {queue.length} offline sale{queue.length === 1 ? "" : "s"} waiting to sync</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="pos-product-grid">
          {sellable.map((p) => (
            <button key={p.id} data-testid={`pos-tile-${p.id}`} onClick={() => tapProduct(p)}
              className="rounded-2xl bg-[#161616] border border-white/10 overflow-hidden text-left active:scale-[0.96] transition-transform">
              <img src={p.image} alt={p.name} className="w-full h-24 sm:h-28 object-cover pointer-events-none" />
              <div className="p-3">
                <p className="text-sm font-semibold text-white leading-tight line-clamp-2">{p.name}</p>
                <p className="text-amber-300 font-bold mt-1">${p.price.toFixed(2)}</p>
              </div>
            </button>
          ))}
          {sellable.length === 0 && <p className="text-zinc-500 text-sm col-span-full py-10 text-center">Nothing sellable in this brand yet.</p>}
        </div>
      </div>

      <aside className="lg:w-[380px] shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 bg-[#111] flex flex-col" data-testid="pos-ticket">
        {paid ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center" data-testid="pos-success">
            <span className="w-16 h-16 rounded-full bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-300" />
            </span>
            <h2 className="font-extrabold text-2xl text-white">${paid.total.toFixed(2)} — {paid.method === "cash" ? "Cash" : "Card"}</h2>
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm text-zinc-400">
                {paid.offline ? (
                  <>Saved <span className="text-amber-300 font-semibold">offline</span> — syncs automatically when back online</>
                ) : (
                  <>Order <span className="text-amber-300 font-semibold">#{paid.order_id.slice(0, 8)}</span> recorded as paid</>
                )}
              </p>
              {paid.hasLemonade && (
                <button
                  data-testid="pos-reprint-stickers"
                  onClick={() => navigate(`/print/${paid.order_id}`)}
                  className="w-8 h-8 rounded-lg border border-white/20 text-zinc-300 hover:border-amber-400/60 hover:text-amber-300 flex items-center justify-center active:scale-95 transition-all"
                  aria-label="Reprint cup stickers"
                  title="Reprint cup stickers"
                >
                  <Printer className="w-4 h-4" />
                </button>
              )}
            </div>
            {paid.hasLemonade && (
              <Link to={`/print/${paid.order_id}`} data-testid="pos-print-stickers" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black font-bold py-4 active:scale-95 transition-transform">
                <Printer className="w-5 h-5" /> Print Cup Stickers
              </Link>
            )}
            <button data-testid="pos-new-order" onClick={() => setPaid(null)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 text-white font-bold py-4 active:scale-95 transition-transform">
              <RotateCcw className="w-5 h-5" /> New Order
            </button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-white/10">
              <h2 className="font-extrabold text-white">Current Ticket</h2>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[120px]" data-testid="pos-ticket-items">
              {ticket.length === 0 && <p className="text-sm text-zinc-600 text-center py-8">Tap products to build the order</p>}
              {ticket.map((t) => (
                <div key={t.key} className="rounded-xl border border-white/10 bg-[#181818] p-3 flex items-center gap-3" data-testid={`pos-line-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{t.name}</p>
                    {t.variant && <p className="text-xs text-zinc-500 truncate">{t.variant}</p>}
                    <p className="text-xs text-amber-300 font-bold">${(t.price * t.qty).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(t.key, t.qty - 1)} className="w-9 h-9 rounded-lg border border-white/15 text-zinc-300 flex items-center justify-center" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
                    <span className="text-white font-bold w-5 text-center">{t.qty}</span>
                    <button onClick={() => setQty(t.key, t.qty + 1)} className="w-9 h-9 rounded-lg border border-white/15 text-zinc-300 flex items-center justify-center" aria-label="Increase"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 p-5 space-y-4">
              <input data-testid="pos-customer-name" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer name (optional)"
                className="w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none" />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-zinc-400"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
                {tip > 0 && (
                  <div className="flex justify-between text-amber-300" data-testid="pos-tip-line">
                    <span>Tip{customActive ? " (custom)" : ` (${tipPct}%)`}</span><span>${tip.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white font-extrabold text-xl pt-1"><span>Total</span><span data-testid="pos-total">${total.toFixed(2)}</span></div>
              </div>
              {cardChooser ? (
                <div className="space-y-3" data-testid="pos-card-entry-chooser">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 text-center">Add a tip?</p>
                  <div className="grid grid-cols-5 gap-2" data-testid="pos-tip-options">
                    {TIP_OPTIONS.map(([pct, label]) => (
                      <button key={pct} data-testid={`pos-tip-${pct}`} onClick={() => { setTipPct(pct); setTipCustom(""); }}
                        className={`rounded-xl border py-2.5 text-sm font-bold transition-colors ${tipPct === pct && !customActive ? "bg-amber-400 border-amber-400 text-black" : "border-white/15 text-zinc-400"}`}>
                        {label}
                      </button>
                    ))}
                    <button data-testid="pos-tip-custom" onClick={() => { setTipPct(0); if (!customActive) setTipCustom("2.00"); }}
                      className={`rounded-xl border py-2.5 text-sm font-bold transition-colors ${customActive ? "bg-amber-400 border-amber-400 text-black" : "border-white/15 text-zinc-400"}`}>
                      Custom
                    </button>
                  </div>
                  {customActive && (
                    <div className="flex items-center gap-2">
                      <span className="text-amber-300 font-extrabold text-lg">$</span>
                      <input data-testid="pos-tip-custom-input" type="number" min="0" step="0.5" value={tipCustom}
                        onChange={(e) => setTipCustom(e.target.value)} placeholder="Tip amount"
                        className="flex-1 rounded-xl border border-amber-400/40 bg-black/40 px-4 py-3 text-white font-bold focus:border-amber-400 focus:outline-none" />
                    </div>
                  )}
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 text-center">How was the card taken?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button data-testid="pos-card-reader" onClick={() => charge("card", "reader")} disabled={paying}
                      className="rounded-xl bg-white text-black font-bold py-4 active:scale-95 transition-transform disabled:opacity-40">
                      📲 Tap / Reader
                    </button>
                    <button data-testid="pos-card-keyed" onClick={() => charge("card", "keyed")} disabled={paying}
                      className="rounded-xl bg-white text-black font-bold py-4 active:scale-95 transition-transform disabled:opacity-40">
                      ⌨️ Keyed In
                    </button>
                  </div>
                  <div className="rounded-xl border border-pink-500/40 bg-pink-500/10 p-3 space-y-2" data-testid="pos-square-tap-block">
                    <button data-testid="pos-card-square-tap" onClick={startSquareTap} disabled={paying || !online}
                      className="w-full rounded-xl bg-pink-500 text-white font-extrabold py-4 active:scale-95 transition-transform disabled:opacity-40">
                      💳 Tap to Pay — Square app
                    </button>
                    <p className="text-[11px] leading-snug text-zinc-400 text-center">Really charges the card in the Square app (Tap to Pay on iPhone · Square Reader on iPad). The two buttons above only record the sale.</p>
                  </div>
                  <button onClick={() => { setCardChooser(false); setTipPct(0); setTipCustom(""); }} className="w-full text-xs text-zinc-500 hover:text-white py-1" data-testid="pos-card-back">← Back</button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button data-testid="pos-charge-cash" onClick={() => { setTipPct(0); setTipCustom(""); charge("cash"); }} disabled={paying || !ticket.length}
                    className="rounded-xl bg-emerald-500 text-white font-extrabold py-4 text-lg active:scale-95 transition-transform disabled:opacity-40">
                    💵 Cash
                  </button>
                  <button data-testid="pos-charge-card" onClick={() => setCardChooser(true)} disabled={paying || !ticket.length}
                    className="rounded-xl bg-white text-black font-extrabold py-4 text-lg active:scale-95 transition-transform disabled:opacity-40">
                    💳 Card
                  </button>
                </div>
              )}
              {ticket.length > 0 && (
                <button data-testid="pos-clear-ticket" onClick={() => setTicket([])} className="w-full inline-flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-red-400 py-1">
                  <Trash2 className="w-3.5 h-3.5" /> Clear ticket
                </button>
              )}
            </div>
          </>
        )}
      </aside>

      <AnimatePresence>
        {modal && (
          <ConfigModal
            product={modal}
            flavorNames={flavorNames}
            onClose={() => setModal(null)}
            onAdd={(p, variant, qty, addons) => addToTicket(p, variant, qty, addons)}
          />
        )}
        {report && <ReportModal report={report} onClose={() => setReport(null)} />}
      </AnimatePresence>
    </main>
  );
}
